/* eslint camelcase: ["error", {allow: ["is_locked"]}] */
import { Grid } from '@mui/material';
import { HSpacer, RoutalDialog, RoutalPalette, RoutalSelect, VSpacer } from '@nx-smartmonkey/ui';
import React, { useState } from 'react';
import DRIVER_STATUS from '../../constants/Drivers';

import { ConfirmDeleteDialog } from '../../dialogs/ConfirmDeleteDialog';
import ConfirmDialog from '../../dialogs/ConfirmDialog';
import { ConstraintsDialog, ConstraintsType } from '../../dialogs/ConstraintsDialog';
import { StopsToDriverDialog } from '../../dialogs/StopsToDriverDialog';
import { Driver } from '../../domain/driver/Driver';
import { DriverStatus } from '../../domain/driver/DriverStatus';
import { Project } from '../../domain/project/Project';
import { Stop } from '../../domain/stop/Stop';
import { useDeleteDrivers } from '../../hooks/driver/useDeleteDrivers';
import { useLockDrivers } from '../../hooks/driver/useLockDrivers';
import { useOptimizeDrivers } from '../../hooks/driver/useOptimizeDrivers';
import { useSaveDriversAsVehicles } from '../../hooks/driver/useSaveDriversAsVehicles';
import { useUnlockDrivers } from '../../hooks/driver/useUnlockDrivers';
import { useUpdateDriversStatus } from '../../hooks/driver/useUpdateDriversStatus';
import { useRetrieveActivePlan } from '../../hooks/plan/useRetrieveActivePlan';
import { useRetrieveActivePlanDrivers } from '../../hooks/plan/useRetrieveActivePlanDrivers';
import { useRetrieveActivePlanGeoFences } from '../../hooks/plan/useRetrieveActivePlanGeoFences';
import { useRetrieveActivePlanStops } from '../../hooks/plan/useRetrieveActivePlanStops';
import { useUpdateDriverStops } from '../../hooks/plan/useUpdateDriverStops';
import { useRetrieveActiveProject } from '../../hooks/project/useRetrieveActiveProject';
import { useRetrieveRightPanelInfo } from '../../hooks/right-panel/useRetrieveRightPanelInfo';
import { useAsUser } from '../../hooks/session/useAsUser';
import { useRetrieveUserCredentials } from '../../hooks/user/useRetrieveUserCredentials';
import { driverStopsHasAnyWarnings } from '../../pages/planner/shared/OptimizationWarningsTooltip';
import Button from '../../react-components/Button';
import Text from '../../react-components/Text';
import eventsPanel from '../../services/events';
import { MixPanelEvents } from '../../services/mixpanel';
import { ContextualMenu } from './ContextualMenu';

type DriversActionsMenuType = `contextual` | `popover`;

type DriversContextAnchor = {
  screenPosition: {
    top: number;
    left: number;
  };
};

type DriversActionsMenuProps = {
  type?: DriversActionsMenuType;
  planStops?: Stop[];
  planDrivers?: Driver[];
  activeProject?: Project;
  drivers: Driver[];
  contextAnchor?: DriversContextAnchor;
  setContextAnchor?: (context?: DriversContextAnchor) => void;
  showChangeConstraints?: boolean;
  showSaveAsVehicle?: boolean;
  showReoptimizeRoute?: boolean;
  showExchangeStops?: boolean;
  showReverseOrder?: boolean;
  showViewDetails?: boolean;
  showLockRoute?: boolean;
  showUnlockRoute?: boolean;
  showDeleteDriver?: boolean;
  showUpdateDriverStatus?: boolean;
  showEndRoute?: boolean;
  popoverProps?: {
    hideArrow?: boolean;
    style?: React.CSSProperties;
    containerStyle?: React.CSSProperties;
    content?: React.ReactElement;
  };
};

export const DriversActionsMenu = ({
  type = `contextual` as DriversActionsMenuType,
  drivers,
  contextAnchor = {
    screenPosition: {
      top: 0,
      left: 0,
    },
  },
  setContextAnchor = () => {},
  showChangeConstraints = true,
  showSaveAsVehicle = true,
  showReoptimizeRoute = true,
  showExchangeStops = true,
  showReverseOrder = true,
  showViewDetails = true,
  showLockRoute = true,
  showUnlockRoute = true,
  showDeleteDriver = true,
  showUpdateDriverStatus = true,
  showEndRoute = true,
  popoverProps,
}: DriversActionsMenuProps) => {
  const { data: activePlan } = useRetrieveActivePlan();
  const [dialog, setDialog] = useState<
    | `optimization_warnings_dialog`
    | `deleteDrivers`
    | `exchangeStops`
    | `finishRoute`
    | `reopenRoute`
    | `SM_updateDriverStatus`
    | `constraintsDrivers`
    | undefined
  >(undefined);
  const { run: saveDriversAsVehicles } = useSaveDriversAsVehicles();
  const { run: deleteDrivers } = useDeleteDrivers();
  const { run: lockDrivers } = useLockDrivers();
  const { run: unlockDrivers } = useUnlockDrivers();
  const { run: updateDriversStatus } = useUpdateDriversStatus();
  const { run: reoptimizeRoutes } = useOptimizeDrivers();
  const { run: updateDriverStops } = useUpdateDriverStops();
  const { asUser } = useAsUser();

  const [statusToUpdate, setStatusToUpdate] = useState<DriverStatus>(`not_started`);

  const { data: planDrivers } = useRetrieveActivePlanDrivers();
  const { data: planStops } = useRetrieveActivePlanStops();
  const { data: planGeoFences } = useRetrieveActivePlanGeoFences();

  const { data: activeProject } = useRetrieveActiveProject();

  const { setRightPanelInfo } = useRetrieveRightPanelInfo();
  const { data: userCredentials } = useRetrieveUserCredentials();

  if (drivers.length === 0) return null;

  return (
    <>
      {type === `popover` && (
        <RoutalSelect
          id="drivers-select-button-popover"
          hideArrow={popoverProps?.hideArrow}
          style={{
            background: `transparent`,
            ...(popoverProps?.style ?? {}),
          }}
          containerStyle={popoverProps?.containerStyle}
          popoverProps={{
            maxHeight: `unset`,
            options: [
              ...(!showViewDetails || drivers.length !== 1
                ? []
                : [
                    {
                      key: `view_details`,
                      width: 240,
                      onClick: () => {
                        setRightPanelInfo({
                          detailType: `driver`,
                          detailId: `${drivers[0].id}`,
                          detailTab: null,
                        });
                        eventsPanel.driverActionsMenu(MixPanelEvents.driver.actions_menu.view_details);
                      },
                      content: <Text intlId="routes.actions.view_details" style={{ cursor: `pointer` }} />,
                    },
                    null,
                  ]),
              ...(!activeProject || !showChangeConstraints || drivers.length === 0
                ? []
                : [
                    {
                      key: `change_constraints`,
                      width: 240,
                      onClick: () => {
                        setDialog(`constraintsDrivers`);
                        eventsPanel.driverActionsMenu(MixPanelEvents.driver.actions_menu.constraints);
                      },
                      content: <Text intlId="routes.actions.change_constraints" style={{ cursor: `pointer` }} />,
                    },
                  ]),
              ...(!showSaveAsVehicle
                ? []
                : [
                    {
                      key: `save_as_vehicle`,
                      width: 240,
                      disabled: Boolean(drivers[0].vehicle_id),
                      onClick: () => {
                        saveDriversAsVehicles({ driverIds: drivers.map((driver) => driver.id) });
                        eventsPanel.driverActionsMenu(MixPanelEvents.driver.actions_menu.save_as_vehicle);
                      },
                      content: <Text intlId="routes.actions.save_as_vehicle" style={{ cursor: `pointer` }} />,
                    },
                  ]),
              ...(!showReoptimizeRoute || drivers.length !== 1
                ? []
                : [
                    {
                      key: `reoptimize`,
                      width: 240,
                      disabled: Boolean(drivers[0].is_locked),
                      onClick: () => {
                        if (
                          activePlan &&
                          driverStopsHasAnyWarnings({
                            driver: drivers[0],
                            planGeoFences: planGeoFences ?? [],
                            activeProject,
                          })
                        ) {
                          setDialog(`optimization_warnings_dialog`);
                          return;
                        }

                        reoptimizeRoutes({ drivers });

                        eventsPanel.driverActionsMenu(MixPanelEvents.driver.actions_menu.reoptimize);
                      },
                      content: <Text intlId="routes.actions.reoptimize" style={{ cursor: `pointer` }} />,
                    },
                  ]),
              ...(!showExchangeStops
                ? []
                : [
                    {
                      key: `exchange_services`,
                      width: 240,
                      disabled:
                        drivers.length === 0 ||
                        drivers.length > 1 ||
                        (drivers.length === 1 && Boolean(drivers[0].is_locked)),
                      onClick: () => {
                        setDialog(`exchangeStops`);
                      },
                      content: <Text intlId="routes.actions.exchange_services" style={{ cursor: `pointer` }} />,
                    },
                  ]),
              ...(!showReverseOrder
                ? []
                : [
                    {
                      key: `reverse_order`,
                      width: 240,
                      disabled: drivers.length === 0 || (drivers.length === 1 && Boolean(drivers[0].is_locked)),
                      onClick: () => {
                        drivers.forEach((driver) => {
                          updateDriverStops({ driver, stops: driver.stops.reverse(), index: 0 });
                        });
                        eventsPanel.driverActionsMenu(MixPanelEvents.driver.actions_menu.reverse_stops_order);
                      },
                      content: <Text intlId="routes.actions.reverse_order" style={{ cursor: `pointer` }} />,
                    },
                  ]),
              ...(showLockRoute || showUnlockRoute ? [null] : []),
              ...(!showLockRoute
                ? []
                : [
                    {
                      key: `lock_driver`,
                      width: 240,
                      disabled: drivers.length === 0 || (drivers.length === 1 && Boolean(drivers[0].is_locked)),
                      onClick: () => {
                        lockDrivers({ drivers });
                        eventsPanel.driverActionsMenu(MixPanelEvents.driver.actions_menu.lock);
                      },
                      content: <Text intlId="routes.actions.lock_driver" style={{ cursor: `pointer` }} />,
                    },
                  ]),
              ...(!showUnlockRoute
                ? []
                : [
                    {
                      key: `unlock_driver`,
                      width: 240,
                      disabled: drivers.length === 0 || (drivers.length === 1 && !drivers[0].is_locked),
                      onClick: () => {
                        unlockDrivers({ drivers });
                        eventsPanel.driverActionsMenu(MixPanelEvents.driver.actions_menu.unlock);
                      },
                      content: <Text intlId="routes.actions.unlock_driver" style={{ cursor: `pointer` }} />,
                    },
                  ]),
              ...((showEndRoute && drivers.length === 1) || (showUpdateDriverStatus && userCredentials?.isStaff(asUser))
                ? [null]
                : []),
              ...(showEndRoute && drivers.length === 1 && drivers[0].status === `in_transit`
                ? [
                    {
                      key: `finish_route`,
                      width: 240,
                      disabled: Boolean(drivers[0].is_locked),
                      onClick: () => {
                        setDialog(`finishRoute`);
                      },
                      content: <Text intlId="routes.actions.finish_route" style={{ cursor: `pointer` }} />,
                    },
                  ]
                : []),
              ...(showEndRoute && drivers.length === 1 && drivers[0].status === `finished`
                ? [
                    {
                      key: `reopen_route`,
                      width: 240,
                      disabled: Boolean(drivers[0].is_locked),
                      onClick: () => {
                        setDialog(`reopenRoute`);
                      },
                      content: <Text intlId="routes.actions.reopen_route" style={{ cursor: `pointer` }} />,
                    },
                  ]
                : []),
              ...(showUpdateDriverStatus && userCredentials?.isStaff(asUser)
                ? [
                    {
                      key: `update_status`,
                      width: 240,
                      disabled: drivers.length === 1 && Boolean(drivers[0].is_locked),
                      onClick: () => {
                        // Initialize the driver status with the selected driver status.
                        if (drivers.length === 1) {
                          setStatusToUpdate(drivers[0].status);
                        }
                        setDialog(`SM_updateDriverStatus`);
                      },
                      content: <Text intlId="routes.actions.update_status" style={{ cursor: `pointer` }} />,
                    },
                  ]
                : []),
              ...(!showDeleteDriver
                ? []
                : [
                    null,
                    {
                      key: `delete_driver`,
                      width: 240,
                      disabled: drivers.length === 1 && Boolean(drivers[0].is_locked),
                      onClick: () => {
                        setDialog(`deleteDrivers`);
                      },
                      content: (
                        <Text
                          intlId="routes.actions.delete_driver"
                          style={{ color: RoutalPalette.error.medium, cursor: `pointer` }}
                        />
                      ),
                    },
                  ]),
            ],
          }}
        >
          {popoverProps?.content}
        </RoutalSelect>
      )}

      {type === `contextual` && (
        <>
          {contextAnchor && (
            <ContextualMenu
              styledMenuItemStyles={{
                width: `240px`,
              }}
              anchorElementPosition={contextAnchor.screenPosition}
              onClose={() => {
                if (setContextAnchor) {
                  setContextAnchor(undefined);
                }
              }}
              options={[
                ...(showViewDetails && drivers.length === 1
                  ? [
                      {
                        key: `view_details`,
                        disabled: drivers.length !== 1,
                        text: <Text intlId="routes.actions.view_details" style={{ cursor: `pointer` }} />,
                        onClick: (event: any) => {
                          event.stopPropagation();

                          setRightPanelInfo({
                            detailType: `driver`,
                            detailId: `${drivers[0].id}`,
                            detailTab: null,
                          });

                          if (setContextAnchor) {
                            setContextAnchor(undefined);
                          }
                          eventsPanel.driverActionsMenu(MixPanelEvents.driver.actions_menu.view_details);
                        },
                      },
                      null,
                    ]
                  : []),
                ...(activeProject && showChangeConstraints && drivers.length > 0
                  ? [
                      {
                        key: `change_constraints`,
                        text: <Text intlId="routes.actions.change_constraints" style={{ cursor: `pointer` }} />,
                        onClick: (event: any) => {
                          event.stopPropagation();
                          setDialog(`constraintsDrivers`);
                          eventsPanel.driverActionsMenu(MixPanelEvents.driver.actions_menu.constraints);
                        },
                      },
                    ]
                  : []),
                ...(showSaveAsVehicle
                  ? [
                      {
                        key: `save_as_vehicle`,
                        disabled: drivers.length === 1 && Boolean(drivers[0].vehicle_id),
                        text: <Text intlId="routes.actions.save_as_vehicle" style={{ cursor: `pointer` }} />,
                        onClick: (event: any) => {
                          event.stopPropagation();
                          saveDriversAsVehicles({ driverIds: drivers.map((driver) => driver.id) });

                          if (setContextAnchor) {
                            setContextAnchor(undefined);
                          }
                          eventsPanel.driverActionsMenu(MixPanelEvents.driver.actions_menu.save_as_vehicle);
                        },
                      },
                    ]
                  : []),
                ...(showReoptimizeRoute && drivers.length === 1
                  ? [
                      {
                        key: `reoptimize`,
                        disabled: Boolean(drivers[0].is_locked),
                        text: <Text intlId="routes.actions.reoptimize" style={{ cursor: `pointer` }} />,
                        onClick: (event: any) => {
                          event.stopPropagation();
                          if (
                            activePlan &&
                            driverStopsHasAnyWarnings({
                              driver: drivers[0],
                              planGeoFences: planGeoFences ?? [],
                              activeProject,
                            })
                          ) {
                            setDialog(`optimization_warnings_dialog`);
                            return;
                          }

                          reoptimizeRoutes({ drivers });

                          if (setContextAnchor) {
                            setContextAnchor(undefined);
                          }
                          eventsPanel.driverActionsMenu(MixPanelEvents.driver.actions_menu.reoptimize);
                        },
                      },
                    ]
                  : []),
                ...(showExchangeStops
                  ? [
                      {
                        key: `exchange_services`,
                        disabled:
                          drivers.length === 0 ||
                          drivers.length > 1 ||
                          (drivers.length === 1 && Boolean(drivers[0].is_locked)),
                        text: <Text intlId="routes.actions.exchange_services" style={{ cursor: `pointer` }} />,
                        onClick: (event: any) => {
                          event.stopPropagation();
                          setDialog(`exchangeStops`);
                        },
                      },
                    ]
                  : []),
                ...(showReverseOrder
                  ? [
                      {
                        key: `reverse_order`,
                        disabled: drivers.length === 0 || (drivers.length === 1 && Boolean(drivers[0].is_locked)),
                        text: <Text intlId="routes.actions.reverse_order" style={{ cursor: `pointer` }} />,
                        onClick: (event: any) => {
                          event.stopPropagation();

                          updateDriverStops({
                            driver: drivers[0],
                            stops: drivers[0].stops.reverse(),
                            index: 0,
                          });

                          if (setContextAnchor) {
                            setContextAnchor(undefined);
                          }
                          eventsPanel.driverActionsMenu(MixPanelEvents.driver.actions_menu.reverse_stops_order);
                        },
                      },
                    ]
                  : []),

                ...(showLockRoute || showUnlockRoute ? [null] : []),

                ...(showLockRoute
                  ? [
                      {
                        key: `lock_driver`,
                        disabled: drivers.length === 0 || (drivers.length === 1 && Boolean(drivers[0].is_locked)),
                        text: <Text intlId="routes.actions.lock_driver" style={{ cursor: `pointer` }} />,
                        onClick: (event: any) => {
                          event.stopPropagation();
                          lockDrivers({ drivers });

                          if (setContextAnchor) {
                            setContextAnchor(undefined);
                          }
                          eventsPanel.driverActionsMenu(MixPanelEvents.driver.actions_menu.lock);
                        },
                      },
                    ]
                  : []),
                ...(showUnlockRoute
                  ? [
                      {
                        key: `unlock_driver`,
                        disabled: drivers.length === 0 || (drivers.length === 1 && !drivers[0].is_locked),
                        text: <Text intlId="routes.actions.unlock_driver" style={{ cursor: `pointer` }} />,
                        onClick: (event: any) => {
                          event.stopPropagation();
                          unlockDrivers({ drivers });

                          if (setContextAnchor) {
                            setContextAnchor(undefined);
                          }
                          eventsPanel.driverActionsMenu(MixPanelEvents.driver.actions_menu.unlock);
                        },
                      },
                    ]
                  : []),

                ...((showEndRoute && drivers.length === 1) ||
                (showUpdateDriverStatus && userCredentials?.isStaff(asUser))
                  ? [null]
                  : []),

                ...(showEndRoute && drivers.length === 1 && drivers[0].status === `in_transit`
                  ? [
                      {
                        key: `finish_route`,
                        disabled: Boolean(drivers[0].is_locked),
                        text: <Text intlId="routes.actions.finish_route" style={{ cursor: `pointer` }} />,
                        onClick: (event: any) => {
                          event.stopPropagation();
                          setDialog(`finishRoute`);
                        },
                      },
                    ]
                  : []),
                ...(showEndRoute && drivers.length === 1 && drivers[0].status === `finished`
                  ? [
                      {
                        key: `reopen_route`,
                        disabled: Boolean(drivers[0].is_locked),
                        text: <Text intlId="routes.actions.reopen_route" style={{ cursor: `pointer` }} />,
                        onClick: (event: any) => {
                          event.stopPropagation();
                          setDialog(`reopenRoute`);
                        },
                      },
                    ]
                  : []),
                ...(showUpdateDriverStatus && userCredentials?.isStaff(asUser)
                  ? [
                      {
                        key: `update_status`,
                        disabled: drivers.length === 1 && Boolean(drivers[0].is_locked),
                        text: <Text intlId="routes.actions.update_status" style={{ cursor: `pointer` }} />,
                        onClick: (event: any) => {
                          event.stopPropagation();
                          // Initialize the driver status with the selected driver status.
                          if (drivers.length === 1) {
                            setStatusToUpdate(drivers[0].status);
                          }
                          setDialog(`SM_updateDriverStatus`);
                        },
                      },
                    ]
                  : []),
                ...(showDeleteDriver
                  ? [
                      null,
                      {
                        key: `delete_driver`,
                        disabled: drivers.length === 1 && Boolean(drivers[0].is_locked),
                        text: (
                          <Text
                            intlId="routes.actions.delete_driver"
                            style={{ color: RoutalPalette.error.medium, cursor: `pointer` }}
                          />
                        ),
                        onClick: (event: any) => {
                          event.stopPropagation();
                          setDialog(`deleteDrivers`);
                        },
                      },
                    ]
                  : []),
              ]}
            />
          )}
        </>
      )}

      <ConfirmDialog
        title={<Text variant="h6" intlId="planner.plan_details.header.optimization_warnings_dialog.title" />}
        description={<Text intlId="planner.plan_details.header.optimization_warnings_dialog.content" />}
        open={dialog === `optimization_warnings_dialog`}
        onCloseDialog={() => setDialog(undefined)}
        onConfirm={() => {
          setDialog(undefined);

          if (setContextAnchor) {
            setContextAnchor(undefined);
          }

          reoptimizeRoutes({ drivers });
        }}
      />

      <ConfirmDialog
        title={<Text variant="h6" intlId="update_driver_status.finish_route.title" />}
        description={<Text intlId="update_driver_status.finish_route.info" />}
        open={dialog === `finishRoute`}
        onCloseDialog={() => setDialog(undefined)}
        onConfirm={() => {
          setDialog(undefined);

          if (setContextAnchor) {
            setContextAnchor(undefined);
          }

          updateDriversStatus({ drivers, status: DRIVER_STATUS.finished });
          eventsPanel.driverActionsMenu(MixPanelEvents.driver.actions_menu.finish_route);
        }}
      />
      <ConfirmDialog
        title={<Text variant="h6" intlId="update_driver_status.reopen_route.title" />}
        description={<Text intlId="update_driver_status.reopen_route.info" />}
        open={dialog === `reopenRoute`}
        onCloseDialog={() => setDialog(undefined)}
        onConfirm={() => {
          setDialog(undefined);

          if (setContextAnchor) {
            setContextAnchor(undefined);
          }

          updateDriversStatus({ drivers, status: DRIVER_STATUS.inTransit });
          eventsPanel.driverActionsMenu(MixPanelEvents.driver.actions_menu.reopen_route);
        }}
      />

      <ConfirmDeleteDialog
        typeData="routes"
        open={dialog === `deleteDrivers`}
        onCloseDialog={() => {
          setDialog(undefined);
          if (setContextAnchor) {
            setContextAnchor(undefined);
          }
        }}
        data={drivers}
        handleDelete={() => {
          setDialog(undefined);

          if (setContextAnchor) {
            setContextAnchor(undefined);
          }

          const driversToDelete = drivers.filter((r: Driver) => !r.is_locked);

          deleteDrivers({ driverIds: driversToDelete.map((driver) => driver.id) });
          eventsPanel.driverActionsMenu(MixPanelEvents.driver.actions_menu.delete);
        }}
      />

      <StopsToDriverDialog
        type="exchange"
        open={dialog === `exchangeStops`}
        drivers={planDrivers ?? []}
        disabledDriverIds={(planDrivers ?? [])
          .map((d) => d.id)
          .filter((driverId) => drivers.map((d) => d.id).includes(driverId))}
        setDialog={(props: any) => {
          setDialog(props);
          if (setContextAnchor) {
            setContextAnchor(undefined);
          }
        }}
        handleInteractStops={(destinationRoute: any) => {
          setDialog(undefined);

          if (setContextAnchor) {
            setContextAnchor(undefined);
          }

          if (destinationRoute === `missing`) {
            const unassignedStops = (planStops ?? []).filter((stop) => !stop.driver_id);

            updateDriverStops({
              driver: drivers[0],
              stops: unassignedStops,
              index: drivers[0].stops.length,
            });
          } else if (drivers.length !== 1 || drivers[0].id !== destinationRoute.id) {
            const newStopsTo = drivers[0].stops;
            const newStopsFrom = destinationRoute.stops;

            updateDriverStops({
              driver: destinationRoute,
              stops: newStopsTo,
              index: destinationRoute.stops.length,
            });

            updateDriverStops({
              driver: drivers[0],
              stops: newStopsFrom,
              index: drivers[0].stops.length,
            });
          }
          eventsPanel.driverActionsMenu(MixPanelEvents.driver.actions_menu.exchange_stops);
        }}
      />

      {activeProject && (
        <ConstraintsDialog
          typeData="drivers"
          open={dialog === `constraintsDrivers`}
          constraintsMap={[
            { id: `start_location`, type: `location` as ConstraintsType },
            { id: `end_location`, type: `location` as ConstraintsType },
            ...(activeProject?.view?.constraints.time_windows
              ? [{ id: `time_window`, type: `time_window` as ConstraintsType }]
              : []),
            ...(activeProject?.view?.constraints.max_distance
              ? [{ id: `max_distance`, type: `kilometers` as ConstraintsType }]
              : []),
            ...(userCredentials?.isStaff(asUser) ||
            (userCredentials?.projects.test(activeProject.id, `can_update_vehicles`) &&
              activeProject?.view?.constraints.min_distance)
              ? [{ id: `min_distance`, type: `kilometers` as ConstraintsType }]
              : []),
            ...(activeProject?.view?.constraints.max_time
              ? [{ id: `max_time`, type: `minutes` as ConstraintsType }]
              : []),
            ...(userCredentials?.isStaff(asUser) ||
            (userCredentials?.projects.test(activeProject.id, `can_update_vehicles`) &&
              activeProject?.view?.constraints.min_time)
              ? [{ id: `min_time`, type: `minutes` as ConstraintsType }]
              : []),
            ...(activeProject?.view?.constraints.weight
              ? [{ id: `max_weight`, type: `number` as ConstraintsType }]
              : []),
            ...(activeProject?.view?.constraints.volume
              ? [{ id: `max_volume`, type: `number` as ConstraintsType }]
              : []),
            ...(activeProject?.view?.constraints.max_services
              ? [{ id: `max_services`, type: `number` as ConstraintsType }]
              : []),
            ...(activeProject?.view?.constraints.provide_requires
              ? [{ id: `provides`, type: `text_array` as ConstraintsType }]
              : []),
            ...(userCredentials?.subscription.test(`planner.geofences`) && activeProject?.geo_fences_enabled
              ? [{ id: `geo_fences`, type: `geo_fences` as ConstraintsType }]
              : []),
          ]}
          project={activeProject}
          onCloseDialog={() => {
            setDialog(undefined);
            if (setContextAnchor) {
              setContextAnchor(undefined);
            }
          }}
          idsToUpdate={drivers.filter((driver) => !driver.is_locked).map((driver) => driver.id)}
          numberOfElements={drivers.filter((route) => !route.is_locked).length}
          onUpdateConstraints={(updatedDrivers: Driver[]) => {
            setDialog(undefined);
            if (setContextAnchor) {
              setContextAnchor(undefined);
            }
          }}
        />
      )}

      <RoutalDialog
        open={dialog === `SM_updateDriverStatus`}
        onCloseDialog={() => {
          setDialog(undefined);
          setStatusToUpdate(`not_started`);
          if (setContextAnchor) {
            setContextAnchor(undefined);
          }
        }}
        dialogProps={{
          disableBackdropClick: true,
          disableEscapeKeyDown: true,
          maxWidth: `xs`,
        }}
        titleContent={<Text variant="h6" intlId="update_driver_status.admin.title" />}
        footerContent={
          <Grid container justifyContent="flex-end">
            <Button
              variant="flat"
              onClick={() => {
                setDialog(undefined);
                setStatusToUpdate(`not_started`);
                if (setContextAnchor) {
                  setContextAnchor(undefined);
                }
              }}
              intlId="update_driver_status.admin.buttons.cancel"
            />

            <HSpacer small />

            <Button
              intlId="update_driver_status.admin.buttons.confirm"
              onClick={() => {
                const driverToUpdate = drivers.filter((driver) => !driver.is_locked);

                updateDriversStatus({ drivers: driverToUpdate, status: statusToUpdate });

                setDialog(undefined);
                setStatusToUpdate(`not_started`);
                if (setContextAnchor) {
                  setContextAnchor(undefined);
                }
              }}
            />
          </Grid>
        }
      >
        <Text intlId="update_driver_status.admin.info" />

        <VSpacer small />

        <RoutalSelect
          id="driver-select-status-popover"
          style={{
            justifyContent: `space-between`,
          }}
          popoverProps={{
            options: [
              {
                key: `not_started`,
                width: 240,
                onClick: () => {
                  setStatusToUpdate(`not_started`);
                },
                content: (
                  <Grid container direction="row" alignItems="center">
                    <Text intlId="routes.status.not_started" style={{ cursor: `pointer` }} />
                  </Grid>
                ),
              },
              {
                key: `in_transit`,
                width: 240,
                onClick: () => {
                  setStatusToUpdate(`in_transit`);
                },
                content: (
                  <Grid container direction="row" alignItems="center">
                    <Text intlId="routes.status.in_transit" style={{ cursor: `pointer` }} />
                  </Grid>
                ),
              },
              {
                key: `finished`,
                width: 240,
                onClick: () => {
                  setStatusToUpdate(`finished`);
                },
                content: (
                  <Grid container direction="row" alignItems="center">
                    <Text intlId="routes.status.finished" style={{ cursor: `pointer` }} />
                  </Grid>
                ),
              },
            ],
          }}
        >
          <Text intlId={`routes.status.${statusToUpdate}`} style={{ cursor: `pointer` }} />
        </RoutalSelect>
      </RoutalDialog>
    </>
  );
};
